export interface EnvironmentSettings {
  /** DSN sentry integration. If not defined sentry won't be initialised */
  sentryDsn?: string;
  /** Sentry trace sample rate for performance monitoring. Double [0 - 1] */
  sentryTraceSampleRate?: number;
  /** API Key (instrumentation key) for App Insights. Used as a fallback when no connection string is provided. */
  appInsightsApiKey?: string;
  /** Connection string for App Insights. Preferred over the instrumentation key (key-only ingestion is deprecated). */
  appInsightsConnectionString?: string;
  /** App Insights client-side sampling percentage [0-100]. Defaults to 100 (no sampling) when not set. */
  appInsightsSamplingPercentage?: number;
  /** CDN url */
  cdnUrl: string;
  /** Google Tag Manager key */
  gtmKey?: string;
  /** Google Tag Manager data layer name */
  gtmDataLayerName?: string;
  /** Google Tag Manager auth token */
  gtmAuth?: string;
  /** Google Tag Manager preview env */
  gtmPreview?: string;
  gtmCookiesWin?: string;
  /** Google Tag Manager key V4 */
  gtmKeyV4?: string;
  /** Google Tag Manager data layer name V4 */
  gtmDataLayerNameV4?: string;
  /** Google Tag */
  gtag?: string;
  /**
   * Microsoft Clarity project ID. When set (and the analytics
   * `enable-microsoft-clarity` rule-engine flag is on) the Clarity tag is
   * loaded for the tenant. Absent/empty ⇒ Clarity disabled.
   */
  clarityProjectId?: string;
  /** Google Cloud Retail api */
  gcr?: string;
  /** Browser title of the application */
  applicationTitle?: string;
  /** Max upload in Mb */
  maxUploadSize: string;
  /** Multi site supports */
  multiSiteSupport: string;
  /** Builder.io API key */
  builderIOApiKey?: string;
  /** export request config */
  exportSettings?: string;
  /** Enable v2 rule engine url */
  enableV2RuleEngine: boolean;
  /** Enable identity auth token */
  enableIdentityToken: boolean;
  /** Enable public product list feature */
  enablePublicProductList?: boolean | string;
  /** Ionic URL */
  ionicCdnUrl: string;
  /** bestseller, puma etc.. */
  tenant?: string;
  /** test, acc, staging, prod */
  env?: string;
  /** App Domain */
  appDomain?: string;
  intercomAppId?: string;
  cookieConsent?: string;
  basketItemCategoryAttributeKey?: string;
  ordersSettings?: string;
  servicenowVirtualAgent?: string;
  basketItemDivisionAttributeKey?: string;
  /** Product attribute key used for the "Product Planning Group" list grouping */
  productPlanningGroupAttributeKey?: string;
  thirdPartyScripts?: string;
  auth: string;
  theme: string;
  /** Merchandiser chatbot backend URL */
  merchandiserChatbotApiUrl?: string;
  /**
   * When true, product search operations (search, suggest, filters, export)
   * route through the legacy GQL BFF instead of calling REST directly.
   * Set to "true" in *-fe.json to roll back a tenant to GQL while REST is
   * being validated. Defaults to false (REST).
   */
  useGqlForSearch?: boolean;
  /** Set to "true" in *-fe.json to roll back DAB to GQL while REST is being validated. */
  useGqlForDab?: boolean;
  /** Set to "true" in *-fe.json to roll back the combined-product call (loadProduct) to GQL while REST is being validated. */
  useGqlForProduct?: boolean;
  /** Set to "true" in *-fe.json to roll back the slim-product call to GQL while REST is being validated. */
  useGqlForSlimProduct?: boolean;
  /** Set to "true" in *-fe.json to roll back the product-by-barcode call to GQL while REST is being validated. */
  useGqlForProductByBarcode?: boolean;
  /** Set to "true" in *-fe.json to roll back price calls to GQL while REST is being validated. */
  useGqlForPrices?: boolean;
  /** Set to "true" in *-fe.json to roll back account calls to GQL while REST is being validated. */
  useGqlForAccounts?: boolean;
  /**
   * Set to "true" in *-fe.json to roll back the email/accounts selector calls
   * (users, account selector/types, brand orgs) to GQL while REST is being
   * validated. Falls back to `useGqlForAccounts` when unset.
   */
  useGqlForEmailSelector?: boolean;
  /** Set to "true" in *-fe.json to roll back order aggregation calls to GQL while REST is being validated. */
  useGqlForOrders?: boolean;
  /** Set to "true" in *-fe.json to roll back basket calls to GQL while REST is being validated. */
  useGqlForBaskets?: boolean;
  /**
   * Kill-switch for residual GQL call tracking (`ResidualGqlCall` RUM events).
   * Defaults to true (tracking on). Set to "false" in *-fe.json to silence the
   * residualGqlInterceptor for a tenant without a redeploy.
   */
  enableResidualGqlTracking?: boolean;
  /** Account selector query templates migrated from GQL config. */
  accountsSearchSettings?: string;

  // --- GQL → FE config migration: product search keys ---
  // These use colon-namespaced names matching the remote config schema.
  /** Attribute keys rendered in the expand panel. JSON array of strings. */
  'products:DescriptionAttributes'?: string;
  /** Sorting, presets, and filter definitions for product search. JSON object. */
  'products:SearchSettings'?: string;
  /** Title level for products: `"article"` or `"product"`. */
  'products:ProductTitleLevel'?: string;
  /** Attribute key used to display a variant's product ID. */
  'products:ProductIdAttributeToDisplay'?: string;
  /** Size-level attribute keys kept when mapping Size variants. JSON array of strings. */
  'products:productSizeAttributesWhiteList'?: string;

  // --- GQL → FE config migration: DAB keys (Phase 1.3) ---
  /** V1 aggregation request config for DAB. JSON object (AggregationRequest). */
  'products:DabAggregationV1DabConfigKey'?: string;
  /** V1 aggregation request config for DAB history. JSON object (AggregationRequest). */
  'products:DabAggregationV1DabHistoryConfigKey'?: string;
  /** Legacy aggregation config for DAB (used when `useLegacyAggregation=true`). JSON object. */
  dabAggregationConfig?: string;
  // --- GQL → FE config migration: product combined-call keys (Phase 2.1) ---
  /** Icons configuration: popularity threshold and NOOS config. JSON object. */
  'products:ProductIconsConfig'?: string;
  // --- GQL → FE config migration: order-book keys (Phase 5.3) ---
  /** Aggregation filter settings for order-book filters. JSON object. */
  'orders:FilterSettings'?: string;
  /** Aggregation request used for the order list. JSON object. */
  'orders:listSettings'?: string;
  /** Order service settings such as CDN URL and image type. JSON object. */
  'orders:serviceSettings'?: string;
}

/**
 * Settings for the environment: in general vendor configurations.
 * API keys, client id, etc.
 * Will be set before bootstraping in main.ts file
 */
export const environmentSettings: EnvironmentSettings = {
  cdnUrl: 'static',
  multiSiteSupport: 'false',
  maxUploadSize: '0', // 0 means no limit check
  enableV2RuleEngine: false,
  enableIdentityToken: false,
  ionicCdnUrl:
    'https://cdn.jsdelivr.net/npm/@ionic/core@5.6.13/dist/ionic/ionic.esm.js',
  basketItemCategoryAttributeKey: 'category',
  productPlanningGroupAttributeKey: 'productplanninggroup',
  ordersSettings:
    '{"cdnUrl":"","defaultImageType":"", "enableOrderMaterials": "false"}',
  auth: '{needToAgreeToTerms: false}',
  theme: '',
};
